import React, { useState, useEffect } from 'react';
import { FaUsers, FaClock, FaExclamationTriangle, FaCheckCircle, FaHourglassHalf } from 'react-icons/fa';
import { getProgramMembers } from '../../api/programs';
import styles from './CapacityIndicator.module.css';

const CapacityIndicator = ({ program, showDetails = true, size = 'medium' }) => {
  const [currentEnrolled, setCurrentEnrolled] = useState(0);
  const [loading, setLoading] = useState(true);

  // Use backend's currentEnrolled count or fetch real-time count as fallback
  useEffect(() => {
    const setEnrolledCount = async () => {
      if (!program?._id) {
        setLoading(false);
        return;
      }

      setLoading(true);

      // First, try to use the backend's currentEnrolled count if available
      if (program.capacity?.currentEnrolled !== undefined && program.capacity?.currentEnrolled !== null) {
        setCurrentEnrolled(program.capacity.currentEnrolled);
        setLoading(false);
        return;
      }

      // Fallback: fetch real-time count via API (for cases where backend doesn't provide currentEnrolled)
      try {
        const enrolled = await getProgramMembers(program._id, { status: 'Enrolled' });
        const enrolledCount = Array.isArray(enrolled) ? enrolled.length : 0;
        setCurrentEnrolled(enrolledCount);
      } catch (err) {
        console.error('Error fetching enrolled members for capacity:', err);
        setCurrentEnrolled(0);
      } finally {
        setLoading(false);
      }
    };

    setEnrolledCount();
  }, [program?._id, program?.capacity?.maxParticipants, program?.capacity?.minParticipants, program?.capacity?.currentEnrolled, program?._lastUpdated]);

  if (!program?.capacity) {
    return null;
  }

  const { capacity } = program;
  const isUnlimited = !capacity.maxParticipants;
  const maxParticipants = capacity.maxParticipants || 0;
  
  // Calculate utilization
  const utilizationPercent = isUnlimited ? 0 : Math.round((currentEnrolled / maxParticipants) * 100);
  const spotsLeft = isUnlimited ? '∞' : Math.max(0, maxParticipants - currentEnrolled);
  
  // Status indicators
  const isAtCapacity = !isUnlimited && currentEnrolled >= maxParticipants;
  const isApproachingCapacity = !isUnlimited && utilizationPercent >= 80;
  const isBelowMinimum = capacity.minParticipants && currentEnrolled < capacity.minParticipants;

  // Status classes
  const getStatusClass = () => {
    if (isAtCapacity) return styles.atCapacity;
    if (isApproachingCapacity) return styles.approachingCapacity;
    if (isBelowMinimum) return styles.belowMinimum;
    return styles.normal;
  };

  const getStatusIcon = () => {
    if (isAtCapacity) return <FaExclamationTriangle />;
    if (isApproachingCapacity) return <FaHourglassHalf />;
    if (isBelowMinimum) return <FaClock />;
    return <FaCheckCircle />;
  };

  const getStatusText = () => {
    if (isAtCapacity) return 'Program Full';
    if (isApproachingCapacity) return 'Almost Full';
    if (isBelowMinimum) return 'Needs More Participants';
    return 'Available';
  };

  const getStatusEmoji = () => {
    if (isAtCapacity) return '🚫';
    if (isApproachingCapacity) return '⚠️';
    if (isBelowMinimum) return '📢';
    return '✅';
  };

  // Show loading state
  if (loading) {
    return (
      <div className={`${styles.capacityIndicator} ${styles[size]} ${styles.loading}`}>
        <div className={styles.capacityHeader}>
          <div className={styles.statusBadge}>
            <span className={styles.statusEmoji}>⏳</span>
            <div className={styles.statusContent}>
              <span className={styles.statusText}>Loading...</span>
              <div className={styles.capacityNumbers}>
                <span className={styles.currentCount}>-</span>
                {!isUnlimited && (
                  <>
                    <span className={styles.separator}>/</span>
                    <span className={styles.maxCount}>{maxParticipants}</span>
                  </>
                )}
                <span className={styles.participantsLabel}>participants</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.capacityIndicator} ${styles[size]} ${getStatusClass()}`}>
      <div className={styles.capacityHeader}>
        <div className={styles.statusBadge}>
          <span className={styles.statusEmoji}>{getStatusEmoji()}</span>
          <div className={styles.statusContent}>
            <span className={styles.statusText}>{getStatusText()}</span>
            <div className={styles.capacityNumbers}>
              <span className={styles.currentCount}>{currentEnrolled}</span>
              {!isUnlimited && (
                <>
                  <span className={styles.separator}>/</span>
                  <span className={styles.maxCount}>{maxParticipants}</span>
                </>
              )}
              <span className={styles.participantsLabel}>participants</span>
            </div>
          </div>
        </div>
        
        {!isUnlimited && (
          <div className={styles.utilizationBadge}>
            {utilizationPercent}%
          </div>
        )}
      </div>

      {/* Modern Progress Bar */}
      {!isUnlimited && (
        <div className={styles.progressContainer}>
          <div className={styles.progressTrack}>
            <div 
              className={styles.progressFill}
              style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
            />
            <div className={styles.progressGlow} />
          </div>
        </div>
      )}

      {showDetails && (
        <div className={styles.detailsGrid}>
          {!isUnlimited && (
            <div className={styles.detailItem}>
              <FaUsers className={styles.detailIcon} />
              <div className={styles.detailContent}>
                <span className={styles.detailValue}>{spotsLeft}</span>
                <span className={styles.detailLabel}>spots left</span>
              </div>
            </div>
          )}
          
          {capacity.minParticipants && (
            <div className={styles.detailItem}>
              <FaClock className={styles.detailIcon} />
              <div className={styles.detailContent}>
                <span className={styles.detailValue}>{capacity.minParticipants}</span>
                <span className={styles.detailLabel}>minimum</span>
              </div>
            </div>
          )}

          {capacity.waitlistEnabled && (
            <div className={styles.detailItem}>
              <FaHourglassHalf className={styles.detailIcon} />
              <div className={styles.detailContent}>
                <span className={styles.detailValue}>Waitlist</span>
                <span className={styles.detailLabel}>available</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CapacityIndicator;
